// noinspection JSUnusedGlobalSymbols

import {VanillaException} from '@/exceptions/VanillaException';
import {ExceptionsCollector} from '@/exceptions/ExceptionsCollector';
import {PaycheckState} from '@/enums/payoff-manager/PaycheckState.enum';
import {SpecialFeeState} from '@/enums/payoff-manager/SpecialFeeState.enum';
import {Types} from 'mongoose';
import {
    OneTimeSpecialFeeFields,
} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/OneTimeSpecialFeeFields';

export namespace PayoffManagerExceptions {
    /**
     * Exception that is thrown when a status change of a paycheck is invalid.
     * If the invalidSpecialFeeState is set, the exception is thrown because at least one special fee has the given invalid state,
     * otherwise the exception is thrown because the status change is not allowed manually.
     */
    export class InvalidStateChangeException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        constructor(public readonly paycheckState: PaycheckState, public readonly invalidSpecialFeeState?: SpecialFeeState) {
            let desc = `Der Status der Abrechnung konnte nicht zu ${PaycheckState.Record[paycheckState]?.text} geändert werden, da dieser Status nur vom System vergeben werden kann.`;
            if (typeof invalidSpecialFeeState !== 'undefined') {
                desc = `Der Status der Abrechnung konnte nicht zu ${PaycheckState.Record[paycheckState]?.text} geändert werden, da mindestens ein Sonderhonorar den Status ${SpecialFeeState.Record[invalidSpecialFeeState].text} hat.`;
            }
            super('PayoffManagerExceptions', 'Unzulässige Statusänderung', desc, 433);
        }
    }

    export class SetPaidNotAllowedException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        constructor(public readonly paycheckId: Types.ObjectId, public readonly paycheckState: PaycheckState) {
            const paycheckStateText = PaycheckState.Record[paycheckState]?.text || paycheckState;
            const confirmedText = PaycheckState.Record[PaycheckState.CONFIRMED]?.text || PaycheckState.CONFIRMED;
            super('PayoffManagerExceptions', 'Unzulässige Statusänderung',
                `Die Abrechnung hat den Status ${paycheckStateText} und ist damit nicht zum Export für Sammelüberweisungen zugelassen.
                Nur Abrechnungen mit dem Status ${confirmedText} dürfen exportiert werden.`
                , 403);
        }

        public get furtherInformationHTML(): string {
            return `<p>ID der Abrechnung: <code>${this.paycheckId}</code></p><p>Status: ${PaycheckState.Record[this.paycheckState]?.text || this.paycheckState}</p>`;
        }
    }

    export class SpecialFeeNotFoundException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        constructor(public readonly paycheckId: Types.ObjectId, public readonly specialFeeId: Types.ObjectId) {
            super('PayoffManagerExceptions', 'Sonderhonorar nicht gefunden', 'Das gewählte Sonderhonorar konnte in der Abrechnung nicht gefunden werden', 433);
        }

        public get furtherInformationHTML(): string {
            return `<p>ID der Abrechnung: <code>${this.paycheckId}</code></p><p>ID des Sonderhonorars: ${this.specialFeeId}</p>`;
        }
    }

    /**
     * No employee contract for the billed period. Newer backends: 409 + code EMPLOYEE_CONTRACT_MISSING + officeInformed (the
     * office got a to-do); older ones: 404 without both – detected by the type either way.
     */
    export class MissingContractException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        public readonly code?: string;
        public readonly officeInformed?: boolean;

        constructor(public readonly employeeId: Types.ObjectId) {
            super('PayoffManagerExceptions',
                'Vertrag nicht gefunden',
                `Für das Profil des Mitarbeitenden konnte kein Vertrag gefunden werden. Daher ist es nicht möglich, eine Abrechnung zu erstellen.`,
                409);
        }

        public get furtherInformationHTML(): string {
            return `<p>ID des Profils: <code>${this.employeeId}</code></p>`;
        }
    }

    /**
     * Exception that is thrown when a paycheck is sealed and no further changes are allowed.
     */
    export class PaycheckSealedException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        constructor(public readonly paycheckId: Types.ObjectId | string, public readonly paycheckState: PaycheckState, public readonly invoiceNumber?: string) {
            super('PayoffManagerExceptions',
                'Bearbeitung nicht möglich',
                `Die Abrechnung ${invoiceNumber || paycheckId} hat den Status ${PaycheckState.Record[paycheckState]?.text} und ist damit vor Änderungen geschützt. Es sind keine weiteren Änderungen möglich.`,
                404);
        }

        public get furtherInformationHTML(): string {
            return `<p>ID der Abrechnung: <code>${this.paycheckId}</code></p><p>Status: ${PaycheckState.Record[this.paycheckState]?.text}</p>`;
        }
    }

    /**
     * Exception that is thrown when a paycheck will have a total of 0 after changes are applied.
     */
    export class PaycheckTotalZeroException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        constructor(public readonly paycheckId: Types.ObjectId | string, public readonly invoiceNumber?: string) {
            super(
                'PayoffManagerExceptions',
                'Rechnung ohne Betrag',
                `Der Vorgang wurde abgebrochen, da die Rechnung ${invoiceNumber || paycheckId} ansonsten einen Gesamtbetrag von 0,00 € hätte.`,
                433);
        }

        public get furtherInformationHTML(): string {
            return `<p>ID der Abrechnung: <code>${this.paycheckId}</code></p><p>Rechnungsnummer: ${this.invoiceNumber || 'Unbekannt'}</p>`;
        }
    }

    /**
     * Exception that is thrown when a paycheck is billed and there is an attempt
     * to modify properties that are not allowed to be changed after the paycheck is billed.
     */
    export class OneTimeSpecialFeeAlreadyBilledException extends VanillaException {
        public readonly fields: OneTimeSpecialFeeFields[];

        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        /**
         * Creates a new instance of the exception.
         * @param id The ID of the special fee that is already billed.
         * @param fields The fields that are not allowed to be changed.
         */
        constructor(public readonly id: Types.ObjectId, ...fields: OneTimeSpecialFeeFields[]) {
            const fieldName = fields.length ? OneTimeSpecialFeeFields.Record[fields[0]]?.text : undefined;

            super(
                'PayoffManagerExceptions',
                'Aktualisierung nicht erlaubt',
                `Dieses Sonderhonorar wurde bereits verrechnet, weshalb ${fieldName ? `das Feld ${fieldName} nicht mehr bearbeitet werden` : 'manche Felder nicht mehr bearbeitet werden können'} und das Honorar nicht mehr gelöscht werden kann.`,
                433);
            this.fields = fields;
        }

        public get furtherInformationHTML(): string {
            if (this.fields.length) {
                let resp = `<p>ID des Sonderhonorars: <code>${this.id}</code></p><p>Gesperrte Felder:</p><ul>`;
                for (const field of this.fields) {
                    const fieldName = OneTimeSpecialFeeFields.Record[field]?.text;
                    resp += `<li>${fieldName ? fieldName : `<code>${field}</code>`}</li>`;
                }
                resp += `</ul>`;
                return resp;
            }
            return super.furtherInformationHTML;
        }
    }

    /**
     * Exception that is thrown when attempting to remove the withdrawOnNextPaycheck flag from a special fee that is already withdrawn.
     */
    export class OneTimeSpecialFeeAlreadyWithdrawnException extends VanillaException {
        static {
            ExceptionsCollector.registerException('PayoffManagerExceptions', this);
        }

        /**
         * Creates a new instance of the exception.
         * @param id The ID of the special fee that is already paid.
         */
        constructor(public readonly id: Types.ObjectId) {
            const fieldName = OneTimeSpecialFeeFields.Record[OneTimeSpecialFeeFields.WITHDRAW_ON_NEXT_PAYCHECK]?.text;

            super(
                'PayoffManagerExceptions',
                'Aktualisierung nicht erlaubt',
                `Dieses Sonderhonorar wurde bereits abgezogen, weshalb das Feld "${fieldName}" nicht mehr bearbeitet werden kann.`,
                433);
        }

        public get furtherInformationHTML(): string {
            return `<p>ID des Sonderhonorars: <code>${this.id}</code></p>`;
        }
    }
}
